import * as React from "react";
import { styled } from "@mui/material/styles";
import { Button, Grid, Typography } from "@mui/material";
import Breadcrumbs from "../../containers/Breadcrumbs";
import ReturnToLandingPageButton from "../../containers/ReturnToLandingPageButton";
import BackButton from "../../containers/BackButton";
import { KeyValue, Query } from "../../interfaces";
import {
  dynamicSearchList,
  explicitSearchList,
  formatId,
} from "../../refdata/constants";
import { AppUrl } from "../../config";
import { useNavigate } from "react-router-dom";
import BuildPeerGroups from "../../containers/BuildPeerGroups";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const PREFIX = "DefineCustomPeerGroups";

const classes = {
  listItem: `${PREFIX}-listItem`,
  button: `${PREFIX}-button`,
  margin: `${PREFIX}-margin`,
  listNormalWeight: `${PREFIX}-listNormalWeight`,
  IndentedList: `${PREFIX}-IndentedList`,
  defineCustomText: `${PREFIX}-defineCustomText`,
  cancelButton: `${PREFIX}-cancelButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.listItem}`]: {
    "margin": 0,
    "& h3": {
      paddingBottom: 0,
    },
    "& ul": {
      padding: 0,
    },
  },

  [`& .${classes.button}`]: {
    alignItems: "center",
    fontSize: "1rem",
    padding: 0,
    fontWeight: 400,
    margin: 0,
    width: 380,
    height: 40,
  },

  [`& .${classes.margin}`]: {
    marginTop: "1rem",
    marginBottom: "1rem",
  },

  [`& .${classes.listNormalWeight}`]: {
    marginLeft: 8,
    fontSize: "1rem",
    color: theme.palette.primary.main,
    fontWeight: "normal",
    paddingBottom: 0,
  },

  [`& .${classes.IndentedList}`]: {
    listStyle: "circle",
    color: theme.palette.primary.main,
    marginLeft: 40,
    lineHeight: "24px",
  },

  [`& .${classes.defineCustomText}`]: {
    color: theme.palette.primary.main,
    marginBottom: "2rem",
  },

  [`& .${classes.cancelButton}`]: {
    fontSize: "1rem",
    fontWeight: 400,
    width: 180,
    height: 40,
    margin: 0,
    padding: 0,
  },
}));

interface Props {
  appState: Query;
  resetState: () => void;
}

export default function DefineCustomPeerGroups({
  appState,
  resetState,
}: Props) {
  const navigate = useNavigate();
  const [displaySearch, setDisplaySearch] = React.useState(false);
  const [displayCert, setDisplayCert] = React.useState(false);

  function createGroupSearch() {
    setDisplaySearch(true);
    navigate("?createGroupType=search");
  }
  function createGroupCert() {
    setDisplayCert(true);
    navigate("?createGroupType=cert");
  }

  React.useEffect(() => {
    if (!window.location.search) {
      resetState();
    }
  }, [resetState]);
  const button = (label: string, className: string, onClick: () => void) => {
    return (
      <Grid
        container
        className={classes.margin}
        sx={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          className={className}
          id={label.toLowerCase().replace(/ /g, "-")}
          variant="contained"
          color="secondary"
          onClick={onClick}
        >
          {label}
        </Button>
      </Grid>
    );
  };

  return (
    <Root>
      <Grid container spacing={2}>
        <Grid size={11}>
          <Breadcrumbs currentPageBreadCrumbTitle="Define Custom Peer Groups" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <ReturnToLandingPageButton />
      <BackButton
        app="FinancialReporting"
        buttonLabel="Back to Financial Reporting"
        variant="dark"
      />
      {displaySearch || appState.ui.createGroupType === "search" ? (
        <BuildPeerGroups subHeader="Dynamic Search Criteria Method" />
      ) : displayCert || appState.ui.createGroupType === "cert" ? (
        <BuildPeerGroups
          subHeader="Explicit Cert List Method"
          includeCheckbox
        />
      ) : (
        <Grid
          container
          spacing={2}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid
            size={{
              md: 8,
            }}
          >
            <Typography variant="h1" style={{ textAlign: "center" }}>
              Defining Custom Peer Groups
            </Typography>
            <Typography variant="body2" className={classes.defineCustomText}>
              How you choose to define a Custom Peer Group will have a great
              impact on the information that peer group reveals. One method
              isn’t better than another. You should consider your goals and what
              information you wish to reveal in order to choose the peer group
              definition method that makes the most sense. Keep in mind, you are
              not limited to the amount of peer groups you can create.
            </Typography>
            <Grid container spacing={4}>
              <Grid
                size={{
                  md: 6,
                }}
              >
                {button(
                  "Define with Dynamic Search Criteria",
                  classes.button,
                  createGroupSearch,
                )}

                <ul className={classes.listItem}>
                  {dynamicSearchList &&
                    dynamicSearchList.map((list: KeyValue) =>
                      list.title.length
                        ? list.title.map((element: KeyValue, i: number) => (
                            <li key={`${formatId(element.subHeading)}-${i}`}>
                              <Typography className={classes.listNormalWeight}>
                                {element.subHeading}
                              </Typography>
                              {element.subTitle.length ? (
                                <ul>
                                  {element.subTitle.map(
                                    (subTitle: string, i: number) => (
                                      <li
                                        key={`${formatId(subTitle)}-${i}`}
                                        className={classes.IndentedList}
                                      >
                                        {subTitle}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              ) : (
                                ""
                              )}
                            </li>
                          ))
                        : "",
                    )}
                </ul>
              </Grid>

              <Grid
                size={{
                  md: 6,
                }}
              >
                {button(
                  "Define with an Explicit Search List",
                  classes.button,
                  createGroupCert,
                )}
                <ul className={classes.listItem}>
                  {explicitSearchList &&
                    explicitSearchList.map((listItem: string, i: number) => (
                      <li key={`${formatId(listItem)}-${i}`}>
                        <Typography className={classes.listNormalWeight}>
                          {listItem}
                        </Typography>
                      </li>
                    ))}
                </ul>
              </Grid>
            </Grid>
            <Grid
              style={{ marginTop: "2rem" }}
              container
              spacing={4}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                onClick={() => {
                  navigate(
                    {
                      pathname: `${AppUrl("PeerGroup")}/manage`,
                      search: "?tab=Custom Peer Groups",
                    },
                    {
                      state: {
                        url: AppUrl("PeerGroup") + window.location.search,
                      },
                    },
                  );
                }}
                id="return-to-manage-page"
                className={classes.cancelButton}
                variant="contained"
                color="inherit"
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Root>
  );
}
